import { useEffect } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { MytaverseLogger } from '@helpers/logger';
import {
  LastWebsocketJsonMessageType,
  WebsocketAction,
} from '@interfaces/webSocketConnectionInfo';

/**
 * The room's assistant fallback identity was edited in the CMS.
 *
 * Without this the values reach the hub only in the profile payload fetched on
 * entering the experience, so an edit stayed invisible until the participant
 * came back in. The backend broadcasts the two fields to every hub in the room.
 *
 * Applying it is `setRoomAssistantIdentity`'s job, which writes `rooms` only —
 * see there for why `currentRoom` must not be touched for this.
 *
 * @param lastJsonMessage - Latest message off the dashboard websocket. Anything
 *   other than `ROOM_UPDATED`, or an update for a room this participant is not
 *   in, is ignored.
 */
export const useHandleRoomUpdatedMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { setRoomAssistantIdentity } = useMytaverseEvent();

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, roomId, assistantIcon, assistantName } = lastJsonMessage;

    if (action !== WebsocketAction.RoomUpdated) {
      return;
    }

    if (!roomId) {
      return;
    }

    MytaverseLogger.log(`Room ${roomId} updated its assistant identity`);

    // Both fields always travel, read off the stored record, so there is no
    // "absent" case to tell apart from a cleared one: `null` is the CMS having
    // removed the value, and falls through to the hub's own default.
    setRoomAssistantIdentity(roomId, {
      assistantIcon: assistantIcon ?? null,
      assistantName: assistantName ?? null,
    });
  }, [lastJsonMessage, setRoomAssistantIdentity]);
};
